import { ClbModal } from '@celebration/react/ClbModal/ClbModal';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import getMessageModalBranch from '../../helpers/get-branch-modal-message';
import ApproveBranchService from './approve-branch-modal.service';
import { useToast } from '../../../../app/contexts/toast/toast.context';
import ProcessingError from '../../../../common/errors/processing-error';
import IApproveOrReproveBranchModalProps from '../../interfaces/IApproveOrReproveBranchModalProps';
import {
    IGetBranchModalMessage,
    ModalAction,
} from '../../helpers/interfaces/IGetBranchModalMessage';

function ApproveBranch({
    opened,
    branches,
    closeModalCallback,
    carrierId,
    fetchBranchesData,
}: IApproveOrReproveBranchModalProps) {
    const { t } = useTranslation();
    const { setToast } = useToast();
    const getSelectedBranches = useCallback(
        () => branches.filter(b => b.checked),
        [branches],
    );
    const [btnLoading, setBtnLoading] = useState<boolean>(false);

    const modalMessage = useMemo(() => {
        const selectedBranches = getSelectedBranches();
        const approveMessage = getMessageModalBranch({
            branches: selectedBranches,
            action: ModalAction.approve,
        } as IGetBranchModalMessage);

        const splitedApproveMessage = approveMessage.split('**');
        const breakedApproveMessage = splitedApproveMessage[2].split('-&-&');

        return (
            <>
                {selectedBranches.length > 1
                    ? t('FilialFraseSelecao1')
                    : t('FilialFraseSelecao2')}
                &nbsp;
                <b>{splitedApproveMessage[1]}</b>
                {breakedApproveMessage[0]}
                <br />
                <br />
                {breakedApproveMessage[1]}
            </>
        );
    }, [getSelectedBranches, t]);

    const modalTitle = useMemo(() => {
        const selectedBranches = getSelectedBranches();

        return selectedBranches.length > 1
            ? t('FilialPluralMaisculo')
            : t('FilialMaisculo');
    }, [getSelectedBranches, t]);

    const closeApproveModal = useCallback(() => {
        if (!btnLoading) {
            closeModalCallback();
        }
    }, [btnLoading, closeModalCallback]);

    const approveConfirmedCallback = useCallback(() => {
        const branchesToApproveChecked = getSelectedBranches();
        setBtnLoading(true);

        ApproveBranchService.ApproveBranch(branchesToApproveChecked, carrierId)
            .then(() => {
                setToast(
                    'positive',
                    branchesToApproveChecked.length === 1
                        ? t('FilialAprovadaSucesso')
                        : t('FilialAprovadasSucesso'),
                );
            })
            .catch(error => {
                if (error instanceof ProcessingError) {
                    setToast(
                        'warning',
                        t('FilialErroAprovacao'),
                        error.message,
                    );
                } else if (error instanceof Error) {
                    setToast(
                        'negative',
                        branchesToApproveChecked.length === 1
                            ? t('FilialNaoAprovada')
                            : t('FilialNaoAprovadas'),
                        error.message,
                    );
                }
            })
            .finally(() => {
                fetchBranchesData();
                setBtnLoading(false);
                closeModalCallback();
            });
    }, [
        carrierId,
        closeModalCallback,
        fetchBranchesData,
        getSelectedBranches,
        setToast,
        t,
    ]);

    const renderCustomBody = () => (
        <span>
            {t('FilialAcaoAcessoSistema')}
            {modalMessage}.
        </span>
    );

    return (
        <ClbModal
            title={`${t('Approve')} ${modalTitle}?`}
            open={opened}
            onClose={() => closeApproveModal()}
            customBody={renderCustomBody}
            type="default"
            data-testid="approve-branch-modal"
            itemsFooter={[
                {
                    key: 'approve-branch-modal-button',
                    id: 'approve-branch-modal-button',
                    label: `${t('Approve')} ${modalTitle}`,
                    disabled: false,
                    loading: btnLoading,
                    title: t('FilialAprovacao'),
                    'data-testid': 'approve-branch-modal-button',
                    onClick: () => approveConfirmedCallback(),
                    role: 'button',
                    size: 'md',
                },
                {
                    key: 'cancel-branch-modal-button',
                    id: 'cancel-branch-modal-button',
                    label: t('Cancelar'),
                    disabled: btnLoading,
                    loading: false,
                    title: t('FilialCancelarReprovacao'),
                    'data-testid': 'cancel-branch-modal-button',
                    onClick: () => closeModalCallback(),
                    role: 'button',
                    size: 'md',
                    styleType: 'secondary',
                },
            ]}
        />
    );
}

export default ApproveBranch;
